import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Trash2, UserPlus } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { listWaiters, createWaiter, deleteWaiter, type WaiterRow } from "@/lib/waiters.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/harikaradmin/waiters")({
  head: () => ({ meta: [{ title: "Garsonlar — JUËN Admin" }] }),
  component: WaitersPage,
});

function WaitersPage() {
  const { user, session, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [waiters, setWaiters] = useState<WaiterRow[]>([]);
  const [fetching, setFetching] = useState(true);
  
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const list = useServerFn(listWaiters);
  const create = useServerFn(createWaiter);
  const remove = useServerFn(deleteWaiter);

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
    if (!loading && user && !isAdmin) void navigate({ to: "/dashboard" });
  }, [loading, user, isAdmin, navigate]);

  const refresh = async () => {
    if (!session?.access_token) return;
    setFetching(true);
    try {
      const rows = await list({ headers: { authorization: `Bearer ${session.access_token}` } });
      setWaiters(rows);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Yükleme hatası");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (isAdmin && session?.access_token) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, session?.access_token]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.access_token) return;
    setSubmitting(true);
    try {
      const uname = username.trim().toLowerCase();
      await create({
        data: { display_name: uname, username: uname, password },
        headers: { authorization: `Bearer ${session.access_token}` },
      });
      toast.success("Garson hesabı oluşturuldu");
      setUsername("");
      setPassword("");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Hata");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId: string, displayName: string) => {
    if (!session?.access_token) return;
    if (!confirm(`${displayName} adlı garsonu silmek istediğine emin misin?`)) return;
    setDeletingId(userId);
    try {
      await remove({
        data: { user_id: userId },
        headers: { authorization: `Bearer ${session.access_token}` },
      });
      toast.success("Garson silindi");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Hata");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/harikaradmin" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Admin
        </Link>

        <h1 className="text-3xl font-bold tracking-tight">Garsonlar</h1>
        <p className="mt-2 text-muted-foreground">
          Garson hesapları yalnızca QR tarayıcıyı kullanabilir. Üye yönetimi gibi diğer admin işlemlerine erişimleri yoktur.
        </p>

        <div className="mt-6">
          <AdminTabs />
        </div>

        <form onSubmit={handleCreate} className="mt-2 grid gap-3 rounded-2xl border border-border bg-card p-5 shadow-card sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <h2 className="font-semibold">Yeni garson ekle</h2>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="w-username">Kullanıcı adı</Label>
            <Input id="w-username" value={username} onChange={(e) => setUsername(e.target.value)} required minLength={3} maxLength={32} placeholder="ör. ahmet" autoCapitalize="none" autoCorrect="off" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="w-pass">Şifre</Label>
            <Input id="w-pass" type="text" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} maxLength={72} placeholder="En az 8 karakter" />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
              Oluştur
            </Button>
          </div>
        </form>

        <div className="mt-6 rounded-2xl border border-border bg-card shadow-card">
          <div className="border-b border-border p-5">
            <h2 className="font-semibold">Mevcut garsonlar</h2>
          </div>
          {fetching ? (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="mx-auto h-5 w-5 animate-spin" />
            </div>
          ) : waiters.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Henüz garson yok.</div>
          ) : (
            <ul className="divide-y divide-border">
              {waiters.map((w) => (
                <li key={w.user_id} className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{w.display_name}</div>
                    <div className="truncate text-xs text-muted-foreground">@{w.username}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleDelete(w.user_id, w.display_name)}
                    disabled={deletingId === w.user_id}
                  >
                    {deletingId === w.user_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-destructive" />
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
